import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { ShareViewOptionsContext, ShareViewOptionsContextType } from './context';
import { 
  ShareViewOptions, 
  defaultShareViewOptions, 
  parseShareViewOptionsFromUrl,
  serializeShareViewOptionsToQuery,
  applyShareViewOptionsPrecedence 
} from '@/utils/urlEncoding';

interface ShareViewOptionsProviderProps {
  children: ReactNode;
}

export const ShareViewOptionsProvider: React.FC<ShareViewOptionsProviderProps> = ({ children }) => {
  // Track SharePanel open state
  const [isSharePanelOpen, setIsSharePanelOpen] = useState(false);
  
  // Initialize from URL parameters or defaults
  const [shareViewOptions, setShareViewOptionsState] = useState<ShareViewOptions>(() => {
    if (typeof window !== 'undefined') {
      const urlOptions = parseShareViewOptionsFromUrl(window.location.search);
      // Determine default based on current path (relative only when not root)
      let computedDefault = true;
      try {
        computedDefault = (window.location.pathname || '/') !== '/';
      } catch {
        computedDefault = true;
      }
      // If the URL explicitly specifies sameOrigin, respect it; otherwise compute a sensible default
      const params = new URLSearchParams(window.location.search);
      const sameOrigin = params.has('sameOrigin') ? urlOptions.sameOrigin : computedDefault;
      // Merge with defaults but force resolved sameOrigin
      const merged: ShareViewOptions = { ...defaultShareViewOptions, ...urlOptions, sameOrigin };
      // For Share UI defaults: admin in shared links should default OFF regardless of current page URL
      merged.admin = false;
      return merged;
    }
    return defaultShareViewOptions;
  });

  // Update URL when options change
  const updateUrl = useCallback((options: ShareViewOptions) => {
    if (typeof window !== 'undefined') {
      const applied = applyShareViewOptionsPrecedence(options);
      const query = serializeShareViewOptionsToQuery(applied);
      
      // Get current URL and update search params
      const url = new URL(window.location.href);
      
      // Parse the query and update params
      const params = new URLSearchParams(query);
      
      // Clear existing share-related params (keep admin separate from current page)
      ['layout', 'funcControls', 'fullscreen', 'tools', 'zoom', 'showUnits', 'header', 'lang', 'showPositiveQuadrant'].forEach(key => {
        url.searchParams.delete(key);
      });
      
      // Add new params (do not write 'admin' to the current page URL)
      params.forEach((value, key) => {
        if (key === 'admin') return;
        url.searchParams.set(key, value);
      });
      
      // Update URL without page reload
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const setShareViewOptions = useCallback((options: ShareViewOptions) => {
    const applied = applyShareViewOptionsPrecedence(options);
    setShareViewOptionsState(applied);
    updateUrl(applied);
  }, [updateUrl]);

  const updateShareViewOption = useCallback(<K extends keyof ShareViewOptions>(
    key: K, 
    value: ShareViewOptions[K]
  ) => {
    const newOptions = { ...shareViewOptions, [key]: value };
    
    // For admin, layout, and lang changes, only update state without immediate URL update
    // They will be applied when SharePanel is closed or URL is manually loaded
    if (key === 'admin' || key === 'layout' || key === 'lang') {
      setShareViewOptionsState(newOptions);
      return;
    }
    
    // For all other options, apply immediately with live effects
    setShareViewOptions(newOptions);
  }, [shareViewOptions, setShareViewOptions]);

  const resetToDefaults = useCallback(() => {
    setShareViewOptions(defaultShareViewOptions);
  }, [setShareViewOptions]);

  const applyPendingChanges = useCallback(() => {
    // Apply admin and layout changes that were pending
    const applied = applyShareViewOptionsPrecedence(shareViewOptions);
    setShareViewOptionsState(applied);
    updateUrl(applied);
  }, [shareViewOptions, updateUrl]);

  const generateShareUrl = useCallback(() => {
    if (typeof window !== 'undefined') {
      // Get current URL without ShareViewOptions params
      const current = new URL(window.location.href);
      
      // Clear existing share-related params from the clone, including admin to avoid inheriting from current URL
      ['layout', 'funcControls', 'fullscreen', 'tools', 'zoom', 'showUnits', 'header', 'lang', 'admin', 'showPositiveQuadrant'].forEach(key => {
        current.searchParams.delete(key);
      });
      
      // Apply current ShareViewOptions state to generate the share URL
      const applied = applyShareViewOptionsPrecedence({ ...shareViewOptions, layout: 'embedded' });
      const query = serializeShareViewOptionsToQuery(applied);
      
      // Add new params from current state; include admin=0 when disabled (for share URL only)
      if (query) {
        const params = new URLSearchParams(query);
        params.forEach((value, key) => {
          current.searchParams.set(key, value);
        });
      }

      // If sameOrigin is enabled, produce a URL relative to current path
      if (shareViewOptions.sameOrigin) {
        return `${current.pathname}${current.search}${current.hash}`;
      }

      return current.toString();
    }
    return '';
  }, [shareViewOptions]);

  const generateEmbedCode = useCallback((width: number | string, height: number | string) => {
    const url = generateShareUrl();
    const widthAttr = typeof width === 'number' ? String(width) : width;
    const heightAttr = typeof height === 'number' ? String(height) : height;
    return `<iframe src="${url}" width="${widthAttr}" height="${heightAttr}" frameborder="0" title="Function Playground"></iframe>`;
  }, [generateShareUrl]);

  // Listen for URL changes (back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      const newOptions = parseShareViewOptionsFromUrl(window.location.search);
      setShareViewOptionsState(newOptions);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const contextValue: ShareViewOptionsContextType = {
    shareViewOptions,
    setShareViewOptions,
    updateShareViewOption,
    resetToDefaults,
    applyPendingChanges,
    generateShareUrl,
    generateEmbedCode,
    isSharePanelOpen,
    setIsSharePanelOpen,
  };

  return (
    <ShareViewOptionsContext.Provider value={contextValue}>
      {children}
    </ShareViewOptionsContext.Provider>
  );
};